import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Trash2, CheckCircle2, AlertTriangle, Pencil, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { ValidatedFlightPatternRow } from "@/utils/scheduleMarkdownParser";

type RowStatus = 'unrecognised' | 'edited' | 'fixed';

interface ModalRow {
  row: ValidatedFlightPatternRow;
  originalIndex: number;
  status: RowStatus;
  fixedClassification?: 'own' | 'handled';
  handledName?: string;
}

function extractPrefix(flightNumber: string): string {
  const firstSegment = flightNumber.split('/')[0];
  const match = firstSegment.match(/^([A-Z]+)/i);
  return match ? match[1].toUpperCase() : '';
}

function extractAllPrefixes(flightNumber: string): string[] {
  return flightNumber.split('/').map(seg => {
    const match = seg.trim().match(/^([A-Z]+)/i);
    return match ? match[1].toUpperCase() : '';
  }).filter(Boolean);
}

interface UnrecognisedRowsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unrecognisedRows: { row: ValidatedFlightPatternRow; index: number }[];
  cityCodes: Set<string>;
  flightPrefix: string;
  handledCodes: Map<string, string>;
  currentOrgId: string;
  currentOrgName: string;
  onApplyFixed: (fixedRows: { row: ValidatedFlightPatternRow; originalIndex: number; classification: 'own' | 'handled'; handledName?: string }[]) => void;
  onDeleteRows: (originalIndices: number[]) => void;
  onHandledCodeRegistered: (code: string, name: string) => void;
}

const DAY_OPTIONS = ['TBA', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Daily'] as const;

const DAY_MAP: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

function classifyPrefix(
  flightNumber: string,
  flightPrefix: string,
  handledCodes: Map<string, string>
): { classification: 'own' | 'handled' | 'unrecognised'; handledName?: string } {
  const prefix = flightNumber.replace(/[^A-Za-z]/g, '').toUpperCase();
  if (flightPrefix && prefix === flightPrefix.toUpperCase()) {
    return { classification: 'own' };
  }
  for (const [code, name] of handledCodes) {
    if (prefix === code.toUpperCase()) {
      return { classification: 'handled', handledName: name };
    }
  }
  return { classification: 'unrecognised' };
}

export function UnrecognisedRowsModal({
  open,
  onOpenChange,
  unrecognisedRows,
  cityCodes,
  flightPrefix,
  handledCodes,
  currentOrgId,
  currentOrgName,
  onApplyFixed,
  onDeleteRows,
  onHandledCodeRegistered,
}: UnrecognisedRowsModalProps) {
  const { toast } = useToast();
  const [modalRows, setModalRows] = useState<ModalRow[]>([]);
  const [deletedIndices, setDeletedIndices] = useState<Set<number>>(new Set());
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerCode, setRegisterCode] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerSaving, setRegisterSaving] = useState(false);

  // Initialize modal rows when opened
  useEffect(() => {
    if (open) {
      setModalRows(
        unrecognisedRows.map(({ row, index }) => ({
          row: {
            ...row,
            dep_time: row.dep_time || 'TBA',
            arr_time: row.arr_time || 'TBA',
          },
          originalIndex: index,
          status: 'unrecognised' as RowStatus,
        }))
      );
      setDeletedIndices(new Set());
    }
  }, [open, unrecognisedRows]);

  const activeRows = modalRows.filter(mr => !deletedIndices.has(mr.originalIndex));

  const updateField = (modalIdx: number, field: string, value: string) => {
    setModalRows(prev => {
      const updated = [...prev];
      const target = { ...updated[modalIdx] };
      const row = { ...target.row };

      if (field === 'flight_number') row.flight_number = value;
      else if (field === 'dep_airport') row.dep_airport = value.toUpperCase().substring(0, 3);
      else if (field === 'dep_time') row.dep_time = value;
      else if (field === 'arr_airport') row.arr_airport = value.toUpperCase().substring(0, 3);
      else if (field === 'arr_time') row.arr_time = value;
      else if (field === 'day') {
        if (value === 'TBA') {
          row.days_bitmask = 0;
          row.days_display = 'TBA';
        } else if (value === 'Daily') {
          row.days_bitmask = 0b1111111;
          row.days_display = 'Daily';
        } else {
          const bit = DAY_MAP[value];
          if (bit !== undefined) {
            row.days_bitmask = 1 << bit;
            row.days_display = value;
          }
        }
      }
      else if (field === 'notes') row.notes = value;

      target.row = row;
      target.status = 'edited';
      target.fixedClassification = undefined;
      target.handledName = undefined;
      updated[modalIdx] = target;
      return updated;
    });
  };

  const validateAll = () => {
    setModalRows(prev =>
      prev.map(mr => {
        if (deletedIndices.has(mr.originalIndex)) return mr;

        const { row } = mr;
        const flightValid = row.flight_number.length >= 2 && row.flight_number.length <= 10;
        const depValid = row.dep_airport.length === 3 && cityCodes.has(row.dep_airport.toUpperCase());
        const arrValid = row.arr_airport.length === 3 && cityCodes.has(row.arr_airport.toUpperCase());
        const depTimeValid = /^([01]\d|2[0-3]):[0-5]\d$/.test(row.dep_time) || row.dep_time.toUpperCase() === 'TBA';
        const arrTimeValid = /^([01]\d|2[0-3]):[0-5]\d$/.test(row.arr_time) || row.arr_time.toUpperCase() === 'TBA';
        const dayValid = row.days_bitmask > 0 || (row.days_display?.toUpperCase() === 'TBA');

        if (!flightValid || !depValid || !arrValid || !depTimeValid || !arrTimeValid || !dayValid) {
          return { ...mr, status: 'edited' as RowStatus, fixedClassification: undefined };
        }

        const cl = classifyPrefix(row.flight_number, flightPrefix, handledCodes);
        if (cl.classification === 'own' || cl.classification === 'handled') {
          // Update validation
          const warnings: string[] = [];
          if (!depValid) warnings.push(`dep airport '${row.dep_airport}' not found`);
          if (!arrValid) warnings.push(`arr airport '${row.arr_airport}' not found`);

          const depReal = row.dep_time.toUpperCase() === 'TBA' ? '' : row.dep_time;
          const arrReal = row.arr_time.toUpperCase() === 'TBA' ? '' : row.arr_time;
          const updatedRow = {
            ...row,
            dep_time: depReal,
            arr_time: arrReal,
            valid: warnings.length === 0,
            warnings,
            is_overnight: depReal && arrReal ? arrReal < depReal : false,
          };

          return {
            ...mr,
            row: updatedRow,
            status: 'fixed' as RowStatus,
            fixedClassification: cl.classification,
            handledName: cl.handledName,
          };
        }

        // Prefix still unrecognised but fields valid
        return { ...mr, status: 'unrecognised' as RowStatus };
      })
    );
  };

  const handleDelete = (originalIndex: number) => {
    setDeletedIndices(prev => new Set([...prev, originalIndex]));
  };

  const fixedRows = activeRows.filter(mr => mr.status === 'fixed');

  const handleApply = () => {
    const toApply = fixedRows.map(mr => ({
      row: mr.row,
      originalIndex: mr.originalIndex,
      classification: mr.fixedClassification!,
      handledName: mr.handledName,
    }));

    const toDelete = Array.from(deletedIndices);

    if (toDelete.length > 0) onDeleteRows(toDelete);
    if (toApply.length > 0) onApplyFixed(toApply);

    // Close if no unrecognised remain
    const remaining = activeRows.filter(mr => mr.status !== 'fixed');
    if (remaining.length === 0) {
      onOpenChange(false);
    }
  };

  const statusIcon = (status: RowStatus) => {
    if (status === 'fixed') return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    if (status === 'edited') return <Pencil className="h-4 w-4 text-amber-500" />;
    return <AlertTriangle className="h-4 w-4 text-red-500" />;
  };

  const statusLabel = (status: RowStatus) => {
    if (status === 'fixed') return '🟢 Fixed';
    if (status === 'edited') return '🟡 Edited';
    return '🔴 Unrecognised';
  };

  // Get current day display for a row
  const getDayValue = (row: ValidatedFlightPatternRow): string => {
    if (row.days_display?.toUpperCase() === 'TBA') return 'TBA';
    if (row.days_display?.toUpperCase() === 'DAILY' || row.days_bitmask === 0b1111111) return 'Daily';
    for (const [name, bit] of Object.entries(DAY_MAP)) {
      if (row.days_bitmask === (1 << bit)) return name;
    }
    return row.days_display || 'TBA';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Review Unrecognised Flights</DialogTitle>
          <DialogDescription>
            Edit any fields to correct these rows. Fixed rows will be re-evaluated and moved to Own or Handled if they now match a known prefix.
          </DialogDescription>
        </DialogHeader>

        {/* Register Handling Relationship */}
        <Collapsible open={registerOpen} onOpenChange={setRegisterOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-1">
              {registerOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              Register a Handling Relationship
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex items-end gap-2 p-3 border rounded-md bg-muted/50 mb-2">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium">Handled code</label>
                <Input
                  value={registerCode}
                  onChange={e => setRegisterCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3))}
                  className="h-7 text-xs font-mono w-16 uppercase"
                  placeholder="CX"
                  maxLength={3}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium">Airline name</label>
                <Input
                  value={registerName}
                  onChange={e => setRegisterName(e.target.value)}
                  className="h-7 text-xs w-40"
                  placeholder="Cathay Pacific"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium">Handled by</label>
                <Input
                  value={currentOrgName || '—'}
                  readOnly
                  className="h-7 text-xs w-24 bg-muted cursor-not-allowed"
                />
              </div>
              <Button
                size="sm"
                className="h-7 text-xs"
                disabled={registerCode.length < 2 || !registerName.trim() || !currentOrgId || registerSaving}
                onClick={async () => {
                  setRegisterSaving(true);
                  try {
                    const { error } = await supabase
                      .from('organisation_handling_relationships')
                      .insert({
                        organisation_id: currentOrgId,
                        handled_code: registerCode.trim().toUpperCase(),
                        handled_name: registerName.trim(),
                        is_active: true,
                      });
                    if (error) throw error;

                    const newCode = registerCode.trim().toUpperCase();
                    const newName = registerName.trim();

                    // Notify parent to update handledCodes map
                    onHandledCodeRegistered(newCode, newName);

                    // Re-classify modal rows with new code
                    let reclassified = 0;
                    setModalRows(prev => prev.map(mr => {
                      if (deletedIndices.has(mr.originalIndex)) return mr;
                      if (mr.status === 'fixed') return mr;

                      const prefixes = extractAllPrefixes(mr.row.flight_number);
                      if (prefixes.includes(newCode)) {
                        reclassified++;
                        return { ...mr, status: 'fixed' as RowStatus, fixedClassification: 'handled', handledName: newName };
                      }
                      return mr;
                    }));

                    toast({
                      title: 'Handling relationship registered',
                      description: `${newCode} registered as handled by ${currentOrgName} — ${reclassified} rows reclassified`,
                    });

                    setRegisterCode('');
                    setRegisterName('');
                    setRegisterOpen(false);
                  } catch (err: any) {
                    toast({ title: 'Error', description: err.message, variant: 'destructive' });
                  } finally {
                    setRegisterSaving(false);
                  }
                }}
              >
                {registerSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save & Re-classify'}
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>
        <div className="flex items-center justify-between mb-2">
          <div className="flex gap-3 text-xs">
            <span>🔴 {activeRows.filter(r => r.status === 'unrecognised').length} unrecognised</span>
            <span>🟡 {activeRows.filter(r => r.status === 'edited').length} edited</span>
            <span>🟢 {fixedRows.length} fixed</span>
            {deletedIndices.size > 0 && <span>🗑 {deletedIndices.size} deleted</span>}
          </div>
          <Button variant="outline" size="sm" onClick={validateAll}>
            Validate All
          </Button>
        </div>

        <div className="overflow-auto flex-1 border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-muted sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left w-10">Status</th>
                <th className="px-2 py-2 text-left">Flight #</th>
                <th className="px-2 py-2 text-left">From</th>
                <th className="px-2 py-2 text-left">Dep</th>
                <th className="px-2 py-2 text-left">To</th>
                <th className="px-2 py-2 text-left">Arr</th>
                <th className="px-2 py-2 text-left">Day</th>
                <th className="px-2 py-2 text-left">Notes</th>
                <th className="px-2 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {modalRows.map((mr, midx) => {
                if (deletedIndices.has(mr.originalIndex)) return null;
                const { row, status } = mr;
                const depAirportInvalid = row.dep_airport.length === 3 && !cityCodes.has(row.dep_airport.toUpperCase());
                const arrAirportInvalid = row.arr_airport.length === 3 && !cityCodes.has(row.arr_airport.toUpperCase());

                return (
                  <tr key={mr.originalIndex} className={
                    status === 'fixed' ? 'bg-green-50 dark:bg-green-950/20' :
                    status === 'edited' ? 'bg-amber-50 dark:bg-amber-950/20' :
                    ''
                  }>
                    <td className="px-2 py-1.5 text-xs" title={statusLabel(status)}>
                      {statusIcon(status)}
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        value={row.flight_number}
                        onChange={e => updateField(midx, 'flight_number', e.target.value)}
                        className="h-7 text-xs font-mono w-24"
                        maxLength={10}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        value={row.dep_airport}
                        onChange={e => updateField(midx, 'dep_airport', e.target.value)}
                        className={`h-7 text-xs font-mono w-16 uppercase ${depAirportInvalid ? 'border-red-500' : ''}`}
                        maxLength={3}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        type="text"
                        value={row.dep_time}
                        onChange={e => updateField(midx, 'dep_time', e.target.value)}
                        className={`h-7 text-xs w-24 font-mono ${row.dep_time.toUpperCase() === 'TBA' ? 'text-muted-foreground italic' : ''}`}
                        placeholder="HH:MM or TBA"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        value={row.arr_airport}
                        onChange={e => updateField(midx, 'arr_airport', e.target.value)}
                        className={`h-7 text-xs font-mono w-16 uppercase ${arrAirportInvalid ? 'border-red-500' : ''}`}
                        maxLength={3}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        type="text"
                        value={row.arr_time}
                        onChange={e => updateField(midx, 'arr_time', e.target.value)}
                        className={`h-7 text-xs w-24 font-mono ${row.arr_time.toUpperCase() === 'TBA' ? 'text-muted-foreground italic' : ''}`}
                        placeholder="HH:MM or TBA"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Select value={getDayValue(row)} onValueChange={v => updateField(midx, 'day', v)}>
                        <SelectTrigger className={`h-7 text-xs w-20 ${getDayValue(row) === 'TBA' ? 'text-muted-foreground italic' : ''}`}>
                          <SelectValue placeholder="Day" />
                        </SelectTrigger>
                        <SelectContent>
                          {DAY_OPTIONS.map(d => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-1 py-1">
                      <Input
                        value={row.notes}
                        onChange={e => updateField(midx, 'notes', e.target.value)}
                        className="h-7 text-xs w-28"
                        placeholder="Optional"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(mr.originalIndex)}
                        title="Delete row"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <DialogFooter className="mt-4 flex justify-between sm:justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Dismiss
          </Button>
          <Button
            onClick={handleApply}
            disabled={fixedRows.length === 0 && deletedIndices.size === 0}
          >
            Apply Fixed Rows ({fixedRows.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
